import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getAuthErrorMessage } from '../../utils/authErrors';
import { getMergedEnrollments } from '../../utils/enrollmentStore';
import { getCertificatesForUser } from '../../services/firebaseCertificates';
import { getBadgesForUser } from '../../services/firebaseBadges';
import './Profile.css';

export default function Profile() {
  const { user, changePassword } = useAuth();
  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    setLoadError('');
    if (!user) {
      setCourses([]);
      setCertificates([]);
      setBadges([]);
      setLoading(false);
      return;
    }
    Promise.all([
      getMergedEnrollments(user.uid),
      getCertificatesForUser(user.uid),
      getBadgesForUser(user.uid),
    ])
      .then(([courseList, certList, badgeList]) => {
        setCourses(courseList || []);
        setCertificates(certList || []);
        setBadges(badgeList || []);
      })
      .catch(() => setLoadError('Could not load your profile data. Please refresh the page.'))
      .finally(() => setLoading(false));
  }, [user]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordMessage('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Please fill in all password fields.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    setPasswordSubmitting(true);
    try {
      const data = await changePassword(currentPassword, newPassword);
      setPasswordMessage(data?.detail || 'Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(getAuthErrorMessage(err));
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const enrolled = courses || [];

  return (
    <div className="profile-page">
      <div className="profile-inner">
        <h1 className="profile-title">Profile and Settings</h1>

        {loadError && <p className="profile-error" role="alert">{loadError}</p>}

        <section className="profile-section">
          <h2 className="profile-section-title">Profile info</h2>
          <div className="profile-card">
            <div className="profile-avatar">{user?.name?.charAt(0) || 'S'}</div>
            <div className="profile-info">
              <p className="profile-name">{user?.name}</p>
              <p className="profile-email">{user?.email}</p>
            </div>
          </div>
        </section>

        <section className="profile-section">
          <h2 className="profile-section-title">Enrolled courses</h2>
          {loading ? (
            <p className="profile-text">Loading your courses…</p>
          ) : enrolled.length === 0 ? (
            <p className="profile-empty">No enrolled courses. <Link to="/">Browse courses</Link></p>
          ) : (
            <>
              <ul className="profile-courses">
                {enrolled.slice(0, 3).map((c) => (
                  <li key={c.id}>
                    <Link to={`/course/${c.id}`}>{c.title}</Link>
                  </li>
                ))}
              </ul>
              {enrolled.length > 3 && (
                <p className="profile-text">
                  and {enrolled.length - 3} more. <Link to="/my-learning" className="profile-link">View all in My Learning</Link>
                </p>
              )}
            </>
          )}
        </section>

        <section className="profile-section">
          <h2 className="profile-section-title">Certificates</h2>
          {loading ? (
            <p className="profile-text">Loading your certificates…</p>
          ) : certificates.length === 0 ? (
            <p className="profile-text">You don’t have any certificates yet. Complete a course to 100% to earn one.</p>
          ) : (
            <p className="profile-text">
              You have {certificates.length} certificate{certificates.length > 1 ? 's' : ''}.
            </p>
          )}
          <Link to="/certificates" className="profile-link">View my certificates</Link>
        </section>

        <section className="profile-section">
          <h2 className="profile-section-title">Achievements & badges</h2>
          {loading ? (
            <p className="profile-text">Loading…</p>
          ) : badges.length === 0 ? (
            <p className="profile-text">No badges yet. Complete courses and leave reviews to earn badges.</p>
          ) : (
            <div className="profile-badges">
              {badges.map((b) => (
                <div key={b.id} className="profile-badge" title={b.description}>
                  <span className="profile-badge-icon">{b.icon}</span>
                  <span className="profile-badge-name">{b.name}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="profile-section">
          <h2 className="profile-section-title">Password and security</h2>
          <div className="profile-card">
            <p className="profile-text">Change your password and manage security settings.</p>
            <button
              type="button"
              className="profile-btn"
              onClick={() => setShowPasswordForm((open) => !open)}
            >
              {showPasswordForm ? 'Close password form' : 'Change password'}
            </button>
            {showPasswordForm && (
              <form className="profile-password-form" onSubmit={handlePasswordSubmit}>
                {passwordError && <p className="profile-error" role="alert">{passwordError}</p>}
                {passwordMessage && <p className="profile-success" role="status">{passwordMessage}</p>}
                <label className="profile-password-label">
                  <span>Current password</span>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="profile-password-input"
                    autoComplete="current-password"
                    required
                  />
                </label>
                <label className="profile-password-label">
                  <span>New password</span>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="profile-password-input"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </label>
                <label className="profile-password-label">
                  <span>Confirm new password</span>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="profile-password-input"
                    autoComplete="new-password"
                    minLength={8}
                    required
                  />
                </label>
                <button
                  type="submit"
                  className="profile-btn profile-btn-secondary"
                  disabled={passwordSubmitting}
                >
                  {passwordSubmitting ? 'Updating password…' : 'Save password'}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
